import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NavbarMenu from "../../../../components/NavbarMenu";
import api from "../../../../api/axiosConfig";

// Components
import ProductoHeader from "../components/ProductoHeader";
import SelectorBase from "../components/SelectorBase";
import ExtrasNormal from "../components/ExtrasNormal";
import ExtrasPorUnidad from "../components/ExtrasPorUnidad";
import CantidadSelector from "../components/CantidadSelector";
import TotalResumen from "../components/TotalResumen";
import AccionesPedido from "../components/AccionesPedido";

// Hooks
import { useExtras } from "../hooks/useExtras";
import { useCarritoProducto } from "../hooks/useCarritoProducto";

export default function PedidoProducto() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  /* ===============================
     PRODUCTO
  ================================ */
  const [producto, setProducto] = useState(state?.producto || null);
  const [loadingProducto, setLoadingProducto] = useState(!state?.producto);

  /* ===============================
     ESTADO BÁSICO
  ================================ */
  const [cantidad, setCantidad] = useState(1);
  const [nota, setNota] = useState("");
  const [base, setBase] = useState("");
  const [personalizarCadaUno, setPersonalizarCadaUno] = useState(false);

  /* ===============================
     CARGAR PRODUCTO SI ENTRA POR URL
  ================================ */
  useEffect(() => {
    let alive = true;

    async function cargarProducto() {
      if (producto) return;
      if (!params?.id) return;

      try {
        setLoadingProducto(true);
        const res = await api.get("/public/productos");
        const data = res.data || [];
        const encontrado = data.find(
          (p) => String(p.id) === String(params.id)
        );
        if (alive) setProducto(encontrado || null);
      } catch (e) {
        console.error("Error cargando producto", e);
        if (alive) setProducto(null);
      } finally {
        if (alive) setLoadingProducto(false);
      }
    }

    cargarProducto();
    return () => {
      alive = false;
    };
  }, [producto, params?.id]);

  /* ===============================
     EXTRAS
  ================================ */
  const {
    esJugo,
    tipoProducto,
    loadingExtras,
    extrasBD,
    extrasQty,
    configPorUnidad,
    setConfigPorUnidad,
    incExtra,
    decExtra,
    incExtraUnidad,
    decExtraUnidad,
    extrasSeleccionados,
    extrasTotal,
    extrasTotalVisual,
  } = useExtras({
    producto,
    cantidad,
    personalizarCadaUno,
  });

  /* ===============================
     CARRITO
  ================================ */
  const {
    lineIdEditando,
    puedeAgregar,
    labelPrincipal,
    agregarCarrito,
  } = useCarritoProducto({
    producto,
    cantidad,
    setCantidad,
    nota,
    setNota,
    base,
    setBase,
    esJugo,
    personalizarCadaUno,
    extrasSeleccionados,
    extrasBD,
    configPorUnidad,
    navigate,
  });

  const precioBase = Number(producto?.precio || 0);

  const tituloExtras =
    tipoProducto === "JUGOS"
      ? "Fruta adicional"
      : tipoProducto === "SNACKS"
      ? "Toppings"
      : "Adicionales";

  if (loadingProducto) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        Cargando producto...
      </div>
    );
  }

  if (!producto) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        Producto no encontrado
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-black text-white">
      <NavbarMenu />

      <div className="pt-28 px-6 max-w-md mx-auto space-y-6 pb-28">
        {/* HEADER */}
        <ProductoHeader
          producto={producto}
          precioBase={precioBase}
          editando={!!lineIdEditando}
        />

        {/* BASE (JUGOS) */}
        {esJugo && !personalizarCadaUno && (
          <SelectorBase base={base} setBase={setBase} />
        )}

        {/* EXTRAS */}
        {!personalizarCadaUno && (
          <ExtrasNormal
            titulo={tituloExtras}
            loading={loadingExtras}
            extrasBD={extrasBD}
            extrasQty={extrasQty}
            extrasSeleccionados={extrasSeleccionados}
            extrasTotal={extrasTotal}
            incExtra={incExtra}
            decExtra={decExtra}
          />
        )}

        {personalizarCadaUno && (
          <ExtrasPorUnidad
            producto={producto}
            esJugo={esJugo}
            loading={loadingExtras}
            extrasBD={extrasBD}
            configPorUnidad={configPorUnidad}
            setConfigPorUnidad={setConfigPorUnidad}
            incExtraUnidad={incExtraUnidad}
            decExtraUnidad={decExtraUnidad}
          />
        )}

        {/* CANTIDAD */}
        <CantidadSelector
          cantidad={cantidad}
          setCantidad={setCantidad}
        />

        {/* TOGGLE PERSONALIZAR */}
        {cantidad > 1 && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={personalizarCadaUno}
              onChange={(e) =>
                setPersonalizarCadaUno(e.target.checked)
              }
              disabled={!!lineIdEditando}
            />
            Personalizar cada uno
          </label>
        )}

        {/* NOTA */}
        {!personalizarCadaUno && (
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Nota para este producto (opcional)"
            className="w-full bg-[#1a1a1a] p-3 rounded"
          />
        )}

        {/* TOTAL */}
        <TotalResumen
          precioBase={precioBase}
          cantidad={cantidad}
          extrasTotal={extrasTotalVisual}
          personalizarCadaUno={personalizarCadaUno}
        />

        {/* ACCIONES */}
        <AccionesPedido
          puedeAgregar={puedeAgregar}
          labelPrincipal={labelPrincipal}
          onAgregar={agregarCarrito}
          onVolver={() =>
            lineIdEditando
              ? navigate("/pedido/carrito")
              : navigate("/pedido")
          }
          onCarrito={() => navigate("/pedido/carrito")}
        />
      </div>
    </section>
  );
}
